
import { ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, AlertTriangle, Lock } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermission?: string;
  requiredModule?: string;
  fallback?: ReactNode;
}

const ProtectedRoute = ({ 
  children, 
  requiredPermission, 
  requiredModule, 
  fallback 
}: ProtectedRouteProps) => {
  const { user, isAuthenticated, isLoading, hasPermission, canAccessModule } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return fallback || (
      <Card className="max-w-md mx-auto mt-8">
        <CardHeader>
          <CardTitle className="flex items-center text-red-600">
            <Lock className="h-5 w-5 mr-2" />
            Access Denied
          </CardTitle>
          <CardDescription>
            You must be logged in to access this resource.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return fallback || (
      <Card className="max-w-md mx-auto mt-8">
        <CardHeader>
          <CardTitle className="flex items-center text-yellow-600">
            <Shield className="h-5 w-5 mr-2" />
            Insufficient Permissions
          </CardTitle>
          <CardDescription>
            You don't have the required permissions to access this resource.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-gray-600">
            <p>Required permission: <code className="bg-gray-100 px-1 rounded">{requiredPermission}</code></p>
            <p className="mt-2">Your permissions:</p>
            <ul className="list-disc list-inside mt-1">
              {user.permissions?.map(permission => (
                <li key={permission} className="text-xs">{permission}</li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (requiredModule && !canAccessModule(requiredModule)) {
    return fallback || (
      <Card className="max-w-md mx-auto mt-8">
        <CardHeader>
          <CardTitle className="flex items-center text-orange-600">
            <AlertTriangle className="h-5 w-5 mr-2" />
            Module Access Restricted
          </CardTitle>
          <CardDescription>
            Your role doesn't have access to this module.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-gray-600">
            <p>Required module: <code className="bg-gray-100 px-1 rounded">{requiredModule}</code></p>
            <p>Your role: <code className="bg-gray-100 px-1 rounded">{user.role}</code></p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
